import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  Loader2,
  ShieldCheck,
  Sparkles,
  Activity,
  ArrowLeft,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import AuthSplitLayout from './AuthSplitLayout';
import { resetPassword } from '../api/api';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (password !== confirmPassword) {
      setError('Şifreler eşleşmiyor.');
      return;
    }

    if (password.length < 6) {
      setError('Şifre en az 6 karakter olmalıdır.');
      return;
    }

    setLoading(true);
    try {
      const response = await resetPassword(email, password);
      setSuccess(response.data?.message || 'Şifreniz başarıyla sıfırlandı.');
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      setError(err.message || 'Şifre sıfırlama başarısız oldu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthSplitLayout
      badge="Şifre Sıfırlama"
      title="Şifrenizi mi unuttunuz?"
      description="E-posta adresinizi girerek şifrenizi hızlıca sıfırlayabilirsiniz."
      dynamicPreview
      sideLabel="Güvenli Erişim"
      sideTitle="Güvenliğinizi Önemsiyoruz"
      sideDescription="Şifrenizi sıfırlayarak hesabınıza güvenle erişmeye devam edebilirsiniz."
      stats={[
        { value: '24/7', label: 'Erişim Desteği' },
        { value: '99.9%', label: 'Güvenlik Oranı' },
        { value: '1 hub', label: 'Kontrol Merkezi' },
      ]}
      highlights={[
        {
          icon: ShieldCheck,
          title: 'Korumalı İşlem',
          description: 'Sıfırlama işlemleriniz uçtan uca şifrelenir.',
        },
        {
          icon: Sparkles,
          title: 'Hızlı Erişim',
          description: 'Beklemeden hemen yeni şifre oluşturun.',
        },
        {
          icon: Activity,
          title: 'Sıfırlama Takibi',
          description: 'Güvenlik amacıyla tüm şifre değişiklikleri günlüğe kaydedilir.',
        },
      ]}
      quote="Hesap güvenliğini sağlamak, başarılı operasyonların ilk adımıdır."
      quoteAuthor="Mailwise Güvenlik Ekibi"
      quoteRole="Bilgi Güvenliği Operasyonları"
    >
      <div className="rounded-[26px] border border-slate-200/80 bg-white/92 p-5 shadow-[0_20px_40px_rgba(15,23,42,0.06)] lg:p-4 xl:p-6">
        <div className="mb-5 flex items-start justify-between gap-4 lg:mb-4">
          <div>
            <p className="text-sm font-semibold text-slate-950">Yeni Şifre Belirle</p>
            <p className="mt-1 text-sm text-slate-500">Lütfen bilgileri eksiksiz doldurun.</p>
          </div>
          <Link to="/login" className="flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900 bg-slate-100 px-3 py-2 rounded-2xl">
            <ArrowLeft className="h-3 w-3" /> Geri Dön
          </Link>
        </div>

        <form onSubmit={handleSubmit} className="grid gap-4 lg:gap-3">
          <div className="grid gap-2 lg:gap-1">
            <Label htmlFor="email" className="text-slate-700 text-xs sm:text-sm lg:text-xs">E-posta Adresi</Label>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                id="email"
                type="email"
                placeholder="name@company.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                className="h-12 rounded-2xl border-slate-200 bg-slate-50 pl-11 pr-4 text-slate-950 placeholder:text-slate-400 focus-visible:ring-sky-500 lg:h-10 lg:text-sm"
              />
            </div>
          </div>

          <div className="grid gap-2 lg:gap-1">
            <Label htmlFor="password" className="text-slate-700 text-xs sm:text-sm lg:text-xs">Yeni Şifre</Label>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                className="h-12 rounded-2xl border-slate-200 bg-slate-50 pl-11 pr-12 text-slate-950 focus-visible:ring-sky-500 lg:h-10 lg:text-sm"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-700"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="grid gap-2 lg:gap-1">
            <Label htmlFor="confirmPassword" className="text-slate-700 text-xs sm:text-sm lg:text-xs">Yeni Şifre Tekrar</Label>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
                className="h-12 rounded-2xl border-slate-200 bg-slate-50 pl-11 pr-12 text-slate-950 focus-visible:ring-sky-500 lg:h-10 lg:text-sm"
              />
            </div>
          </div>

          {error && (
            <Alert variant="destructive" className="border-red-200 bg-red-50 text-red-900">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="border-green-200 bg-green-50 text-green-900">
              <AlertDescription>{success}</AlertDescription>
            </Alert>
          )}

          <Button type="submit" className="h-12 w-full rounded-2xl bg-slate-950 font-semibold text-white shadow-[0_18px_35px_rgba(15,23,42,0.18)] transition-all hover:bg-slate-800 lg:h-10 lg:text-sm" disabled={loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {loading ? 'Güncelleniyor...' : 'Şifreyi Güncelle'}
          </Button>
        </form>
      </div>
    </AuthSplitLayout>
  );
}
